import { classes } from 'html-classes';
import { ElementType, memo } from 'react';

import { VARIANT_CLASSES } from './constants';
import { PolymorphicProps } from './interfaces';
import styles from './Typography.module.scss';

/**
 * Typography component
 * Flexible text component that supports all design system typography variants
 * Optimized with React.memo and lookup objects for better performance
 *
 * @example
 * // Basic usage with header variant
 * <Typography variant={VARIANT['Header 100/Medium']} as="h1">
 *   Heading Text
 * </Typography>
 *
 * @example
 * // Text with custom styling via className
 * <Typography
 *   variant={VARIANT['Text 200/Medium']}
 *   className={styles.successText}
 *   as="span"
 * >
 *   Success message
 * </Typography>
 *
 * @example
 * // Anchor with required href (TypeScript enforced)
 * <Typography
 *   as="a"
 *   href="/path"
 *   variant={VARIANT['Text 200/Medium']}
 * >
 *   Link Text
 * </Typography>
 */
const Typography = memo(
  <T extends ElementType = 'span'>({
    variant,
    as,
    className,
    children,
    ...rest
  }: PolymorphicProps<T>) => {
    const variantClass =
      styles[VARIANT_CLASSES[variant] as keyof typeof styles];

    const finalClassName = classes([
      styles.typography,
      variantClass,
      className,
    ]);

    const Component = (as || 'span') as ElementType;

    return (
      <Component className={finalClassName} {...rest}>
        {children}
      </Component>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
